import axios from "axios";
import Loading from "Components/Loading";
import LoginError from "Pages/Login/LoginError";
import queryString from "query-string";
import { useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useAsync } from "react-use";
import keycloak from "system/keycloak";
import { User } from "system/Domain";
import useUser from "./system/useUser";

export default () => {
	const [user, setUser] = useUser();
	const [apiError, setApiError] = useState<string | null>(null);
	const location = useLocation();
	const history = useHistory();

	const authenticationEvaluation = useAsync(async () => {
		const { search } = location;
		const query = queryString.parse(search);

		// Bypass: a Carlos-minted JWT in the URL skips Keycloak entirely. Used
		// for storage links, customer order forms, and other flows where we hand
		// out direct access tokens to specific resources.
		if (query.access_token) {
			try {
				const { data } = await axios.get("/api/users/whoami", {
					headers: {
						Authorization: `Bearer ${query.access_token}`,
					},
				});

				setUser({
					...data,
					loginToken: {
						token: query.access_token,
					},
				});

				history.replace({
					pathname: location.pathname,
					search: queryString.stringify({
						...query,
						access_token: undefined,
					}),
				});

				return true;
			} catch (e) {
				if (e.isAxiosError && e.response.status === 401 && e.response.headers.authenticationhandler) {
					setApiError(e.response.headers.authenticationhandler);
				} else {
					setApiError(e.message);
				}
				return false;
			}
		}

		if (user) {
			return true;
		}

		const client = keycloak.createClient();

		const handleError = async (e: any) => {
			if (e?.isAxiosError && e.response?.status === 403) {
				setApiError("FORBIDDEN");
			} else {
				setApiError(e?.message ?? "Unknown error");
			}
		};

		const loginWithKeycloak = async () => {
			try {
				const response = await axios.post<User>("/api/login-with-keycloak", { accessToken: client.token });
				setUser({
					...response.data,
				});
				// Persist Keycloak tokens so a later page reload can re-seed the
				// singleton via init({ token, refreshToken, idToken }) — needed so
				// logout() can pass id_token_hint and actually terminate the KC session.
				sessionStorage.setItem(
					"kc-tokens",
					JSON.stringify({
						token: client.token,
						refreshToken: client.refreshToken,
						idToken: client.idToken,
					}),
				);

				return true;
			} catch (e) {
				await handleError(e);
			}
		};

		try {
			// useNonce: false works around a keycloak-js v21 bug where the lib checks
			// `nonce` on every token (access, refresh, id) but Keycloak server only puts
			// nonce on the id_token — undefined != storedNonce trips invalid-nonce and
			// rejects init. Fixed in later keycloak-js; revisit if/when we upgrade.
			const authenticated = await client.init({
				onLoad: "check-sso",
				pkceMethod: "S256",
				checkLoginIframe: false,
				useNonce: false,
			});

			// keycloak-js leaves `iss` in the URL fragment after every successful
			// auth (cosmetic bug, keycloak#22486 — `iss` isn't in its supportedParams
			// list so parseCallback doesn't strip it). Clean it ourselves.
			if (window.location.hash.startsWith("#iss=")) {
				window.history.replaceState(
					window.history.state,
					"",
					window.location.pathname + window.location.search,
				);
			}

			if (authenticated) {
				await loginWithKeycloak();
				return true;
			}

			await client.login({
				// Preserve path+query so a deep-link that bounced through KC lands
				// the user back on the page they originally tried to open. Strip
				// the hash — keycloak-js leaves `iss` in the fragment after each
				// successful auth (cosmetic bug, see keycloak#22486), and OAuth
				// spec forbids fragments in redirect_uri anyway.
				redirectUri: window.location.origin + window.location.pathname + window.location.search,
				scope: "openid profile email",
			});

			// keep the evaluation in loading state during redirect
			await new Promise(() => {});
		} catch (e) {
			await handleError(e);
		}

		return false;
	}, [user]);

	if (authenticationEvaluation.loading) {
		return <Loading />;
	}

	if (!authenticationEvaluation.value) {
		return <LoginError error={apiError || "Unbekannt"} />;
	}

	return null;
};
